import React, { useEffect, useRef, useState } from "react";
import { vehicleService } from "../../Services/vehicle.service";
import { useDispatch } from "react-redux";
import {
    GridComponent,
    Page,
    Inject,
    ColumnDirective,
    ColumnsDirective,
    Filter,
} from "@syncfusion/ej2-react-grids";
import Spinner from "../Components/Spinner";
import DeleteModal from "../Components/Modals/Delete-modal";
import "../../styles/menuNickname.css"
import { useTranslation } from "react-i18next";

function EditAliasModal({ show, onClose, vehicle, onSave, onRemove }) {
    const { t } = useTranslation();
    const [alias, setAlias] = useState(vehicle?.plateAlias || "");
    const [confirmShow, setConfirmShow] = useState(false);

    useEffect(() => {
        setAlias(vehicle?.plateAlias || "");
    }, [vehicle]);

    if (!show) return null;

    const handleRemoveClick = () => setConfirmShow(true);
    const handleCancelRemove = () => setConfirmShow(false);
    const handleConfirmRemove = () => {
        setConfirmShow(false);
        onRemove && onRemove(vehicle);
    };

    return (
        <>
            <div
                className="modal-backdrop menu-nickname "
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: "rgba(0,0,0,0.5)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 1050,
                }}
            >
                <div
                    className="modal-content"
                    style={{ padding: 20, borderRadius: 8, width: 400 }}
                >
                    <h5>{t("VEHICLE_NICKNAME.EDIT_NICKNAME")}</h5>
                    <p>
                        {t("VEHICLE_NICKNAME.PLATE")}: <strong>{vehicle?.licensePlate}</strong>
                    </p>
                    <input
                        type="text"
                        className="form-control"
                        value={alias}
                        onChange={(e) => setAlias(e.target.value)}
                    />
                    <div style={{ marginTop: 16, textAlign: "right" }}>
                        <button
                            className="btn btn-danger"
                            onClick={handleRemoveClick}
                            style={{ marginRight: 8 }}
                        >
                            {t("VEHICLE_NICKNAME.DELETE")}
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={onClose}
                            style={{ marginRight: 8 }}
                        >
                            {t("VEHICLE_NICKNAME.CANCEL")}
                        </button>
                        <button className="btn btn-primary" onClick={() => onSave(alias)}>
                            {t("BUTTONS.SAVE")}
                        </button>
                    </div>
                </div>
            </div>

            <DeleteModal
                show={confirmShow}
                handleClose={handleCancelRemove}
                callBackRemove={handleConfirmRemove}
                itemName={`${t("VEHICLE_NICKNAME.NICKNAME_OF_VEHICLE")} ${vehicle?.licensePlate}`}
                itemRemoveId={vehicle?.vehicleId}
            />
        </>
    );
}

// ===== Componente principal (JSX) =====
export default function VehiclesNickname() {
    const { t } = useTranslation();
    const [dataLoading, setDataLoading] = useState(true);
    const [dataFilterVisits, setDataFilterVisits] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [showModal, setShowModal] = useState(false);

    // Altura e paginação dinâmicos
    const [pageSize, setPageSize] = useState(15);

    const dispatch = useDispatch();

    // Refs para medir layout
    const gridAnchorRef = useRef(null);

    // Constantes de layout (ajuste fino se quiser)
    const ROW_HEIGHT = 30; // altura aproximada da linha da Syncfusion
    const HEADER_PAGER_BUDGET = 100; // cabeçalho da grid + pager
    const BOTTOM_MARGIN = 30; // respiro ao fim da página
    const MIN_PAGE = 5;
    const MAX_PAGE = 100;

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await vehicleService.fetchVehiclesAliases();
                
                // Garante que sempre seja um array válido
                if (Array.isArray(response)) {
                    setDataFilterVisits(response);
                } else {
                    setDataFilterVisits([]);
                    if (response?.error) {
                        console.log("Erro ao buscar apelidos:", response.message || response.friendlyMessage);
                    }
                }
            } catch (error) {
                console.error("Erro ao buscar veículos:", error);
                setDataFilterVisits([]);
            } finally {
                setDataLoading(false);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        dispatch({ type: "title", value: t("VEHICLE_NICKNAME.TITLE") });
    }, [dispatch, t]);

    const calculateGridMetrics = () => {
        const winH = window.innerHeight;
        const top = (gridAnchorRef.current && gridAnchorRef.current.getBoundingClientRect().top) || 0;

        const available = Math.max(50, winH - top - BOTTOM_MARGIN);
        const rowsArea = Math.max(50, available - HEADER_PAGER_BUDGET);
        const rowsFit = Math.floor(rowsArea / ROW_HEIGHT);

        // total de itens (para este grid é o mesmo do dataSource do componente)
        const total = Array.isArray(dataFilterVisits) ? dataFilterVisits.length : 0;

        // se couber tudo, mostra tudo (e evita 2ª página)
        const targetRows = Math.max(MIN_PAGE, Math.min(rowsFit, total, MAX_PAGE));

        setPageSize(targetRows);
    };

    // Debounce simples para resize + recálculo ao terminar loading
    useEffect(() => {
        if (!dataLoading) calculateGridMetrics();

        let t = null;
        const onResize = () => {
            if (t) window.clearTimeout(t);
            t = window.setTimeout(() => calculateGridMetrics(), 50);
        };

        window.addEventListener("resize", onResize);
        return () => {
            window.removeEventListener("resize", onResize);
            if (t) window.clearTimeout(t);
        };
    }, [dataLoading]);

    const handleRowSelected = (args) => {
        setSelectedVehicle(args.data);
        setShowModal(true);
    };

    const handleSaveAlias = async (newAlias) => {
        try {
            await vehicleService.updateVehicleAlias(selectedVehicle.vehicleId, newAlias);
            setDataFilterVisits((prev) =>
                prev.map((item) =>
                    item.licensePlate === selectedVehicle.licensePlate
                        ? { ...item, plateAlias: newAlias }
                        : item
                )
            );
            setShowModal(false);
        } catch (error) {
            console.error("Erro ao salvar apelido:", error);
        }
    };

    const handleRemoveAlias = async () => {
        try {
            await vehicleService.removeVehicleAlias(selectedVehicle.vehicleId);
            setDataFilterVisits((prev) =>
                prev.map((item) =>
                    item.licensePlate === selectedVehicle.licensePlate
                        ? { ...item, plateAlias: "" }
                        : item
                )
            );
            setShowModal(false);
        } catch (error) {
            console.error("Erro ao remover apelido:", error);
        }
    };

    return (
        <div className="card grid-card" style={{ margin: 20, padding: 20 }}>
            {/* Âncora para medir posição do grid na viewport */}
            <div ref={gridAnchorRef} />

            {dataLoading ? (
                <Spinner />
            ) : (
                <GridComponent
                    dataSource={dataFilterVisits}
                    allowPaging
                    allowFiltering={true}
                    filterSettings={{ type: "Menu" }}
                    pageSettings={{ pageSize }}
                    rowHeight={ROW_HEIGHT}                // <- importantíssimo pra bater com o cálculo
                    height={"auto"}                      // altura justa, sem "sobras"
                    style={{ marginTop: 16 }}
                    locale="pt-BR"
                    rowSelected={handleRowSelected}
                    rowDataBound={(args) => {
                        args.row.style.cursor = "pointer";
                    }}
                    actionComplete={() => {
                        // revalida após paginação/filtragem/ordenar
                        setTimeout(calculateGridMetrics, 50);
                    }}
                >
                    <ColumnsDirective>
                        <ColumnDirective field="licensePlate" headerText={t("VEHICLE_NICKNAME.PLATE")} width="130" />
                        <ColumnDirective field="plateAlias" headerText={t("VEHICLE_NICKNAME.NICKNAME")} width="100" />
                    </ColumnsDirective>
                    <Inject services={[Page, Filter]} />
                </GridComponent>
            )}

            <EditAliasModal
                show={showModal}
                onClose={() => setShowModal(false)}
                vehicle={selectedVehicle}
                onSave={handleSaveAlias}
                onRemove={handleRemoveAlias}
            />
        </div>
    );
}